import * as React from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useState, useEffect } from "react";

function createData(
  giftCardNum,
  customerEmail,
  giftcardValue,
  createdAt,
  giftcardstatus,
  physicalCardNum,
) {
  return {
    giftCardNum,
    customerEmail,
    giftcardValue,
    giftcardstatus,
    physicalCardNum,
    createdAt: `${new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).format(new Date(createdAt))}`,
  };
}

export default function GiftcardTable(props) {
  const giftcardData = props.giftcardData;
  const [selectedCard, setSelectedCard] = useState(null);
  const [physicalCardNumber, setPhysicalCardNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([
    createData("No Data", "N/A", 0, new Date()),
  ]);

  useEffect(() => {
    let rawData = [];
    if (giftcardData.length !== 0) {
      giftcardData.map((e) =>
        rawData.push(
          createData(
            e.giftCardNum,
            e.stripeData?.customer_details?.email || "N/A",
            e.giftCardValue,
            e.createdAt,
            e.giftCardStatus,
            e.physicalCardNum,
          ),
        ),
      );
    }
    setRows(rawData);
  }, [giftcardData]);

  const handleActivate = async (event) => {
    event.preventDefault();
    if (saving || !/^[0-9]{6}$/.test(physicalCardNumber)) return;
    setSaving(true);
    setError("");
    try {
      await props.onActivate(selectedCard, physicalCardNumber.trim());
      setSelectedCard(null);
    } catch (err) {
      setError(
        err.message || "Unable to activate gift card. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ maxHeight: `calc(86vh - 180px)` }}
      >
        <Table stickyHeader aria-label="giftcard table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Redemption Code</TableCell>
              <TableCell align="left" sx={{ fontWeight: "bold" }}>
                Customer Email
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                Giftcard Value
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                Order Date
              </TableCell>
              <TableCell align="left" sx={{ fontWeight: "bold" }}>
                STATUS
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell component="th" scope="row">
                  {row.giftCardNum}
                  {row.physicalCardNum != null &&
                    row.physicalCardNum !== "" && (
                      <Typography
                        component="div"
                        variant="body2"
                        color="text.secondary"
                      >
                        Physical card: {row.physicalCardNum}
                      </Typography>
                    )}
                </TableCell>
                <TableCell align="left">{row.customerEmail}</TableCell>
                <TableCell align="right">${row.giftcardValue}</TableCell>
                <TableCell align="right">{row.createdAt}</TableCell>
                <TableCell
                  align="left"
                  sx={{
                    color:
                      row.giftcardstatus?.toLowerCase() === "pending"
                        ? "gray"
                        : "green",
                  }}
                >
                  {row.giftcardstatus}
                </TableCell>
                <TableCell align="right">
                  {row.giftcardstatus?.toLowerCase() === "pending" && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        setSelectedCard(giftcardData[index]);
                        setPhysicalCardNumber("");
                        setError("");
                      }}
                    >
                      Activate
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={Boolean(selectedCard)}
        onClose={() => {
          if (!saving) setSelectedCard(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <form onSubmit={handleActivate}>
          <DialogTitle>Activate gift card</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              required
              fullWidth
              margin="dense"
              label="Physical card number"
              value={physicalCardNumber}
              disabled={saving}
              helperText="Enter exactly 6 digits."
              inputProps={{ inputMode: "numeric", pattern: "[0-9]{6}", maxLength: 6 }}
              onChange={(event) => {
                const value = event.target.value;
                if (/^[0-9]{0,6}$/.test(value)) setPhysicalCardNumber(value);
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={() => setSelectedCard(null)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !/^[0-9]{6}$/.test(physicalCardNumber)}
            >
              {saving ? "Saving..." : "OK"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

GiftcardTable.propTypes = {
  giftcardData: PropTypes.array.isRequired,
  onActivate: PropTypes.func.isRequired,
};
